// src/hooks/useApiWithQueue.js
// Enhanced API hook that integrates with the generation queue.
// Starts jobs via REST, then relies on Firestore listeners (in useGenerationQueue)
// for real-time status updates — no polling from this layer.

import { useCallback } from 'react';
import { useApi } from './useApi';
import { apiService } from '../apiService';

export const useApiWithQueue = (generationQueue) => {
  const api = useApi();

  // Start video generation — adds to queue (which subscribes to Firestore listener)
  const generateVideoWithQueue = useCallback(async (formData, userId) => {
    const tempJobId = `temp-${Date.now()}`;

    try {
      // Add to queue immediately with temporary ID
      generationQueue.addJob({
        jobId: tempJobId,
        type: 'video',
        source: 'home',
        title: formData.topic || 'Untitled Video',
        durationType: formData.duration_type || formData.durationType || 'short',
        metadata: {
          duration: formData.duration,
          level: formData.level,
          durationType: formData.duration_type || formData.durationType || 'short',
          credits: formData.duration_type === 'long' || formData.durationType === 'long' ? 2 : 1
        }
      });

      // Build request matching what apiService.startVideoGeneration expects
      const mapLevelToNumber = (level) => {
        if (typeof level === 'number') return level;
        switch (String(level).toLowerCase()) {
          case 'beginner': case '1': return 1;
          case 'intermediate': case '2': return 2;
          case 'advanced': case '3': return 3;
          default: return 2;
        }
      };

      const videoRequest = {
        topic: String(formData.topic || ''),
        level: mapLevelToNumber(formData.level),
        duration_type: formData.duration_type || formData.durationType || 'short',
        enable_subtitles: Boolean(formData.enableSubtitles),
        subtitle_style: formData.enableSubtitles ? 'modern' : null,
        dry_run: Boolean(formData.dryRun),
        user_id: String(userId),
        model: formData.mode || formData.model || 'mode_fast_37',
      };

      const startResponse = await apiService.startVideoGeneration(videoRequest);
      const actualJobId = startResponse.job_id;

      if (actualJobId && actualJobId !== tempJobId) {
        generationQueue.updateJobId(tempJobId, actualJobId);
      }

      return { job_id: actualJobId };
    } catch (error) {
      console.error('Error in generateVideoWithQueue:', error);
      generationQueue.removeJob(tempJobId);
      throw error;
    }
  }, [generationQueue]);

  // Start problem solving — adds to queue
  const solveProblemWithQueue = useCallback(async (problemText, userId, options = {}) => {
    const tempJobId = `temp-${Date.now()}`;

    try {
      generationQueue.addJob({
        jobId: tempJobId,
        type: 'problem',
        source: 'home',
        title: problemText?.substring(0, 100) || 'Problem Solution',
        metadata: {
          duration: options.duration || options.video_duration || 3,
          level: options.detailLevel || options.detail_level || 2,
          subject: options.subject || 'math',
          credits: 1
        }
      });

      const startResponse = await apiService.startProblemSolving({
        problem_text: problemText,
        user_id: userId,
        detail_level: options.detailLevel || options.detail_level || 2,
        video_duration: options.duration || options.video_duration || 3,
        subject: options.subject || 'math',
        problem_type: options.problemType || options.problem_type || 'homework',
        model: options.model || 'mode_fast_37',
      });

      const actualJobId = startResponse.job_id;
      if (actualJobId && actualJobId !== tempJobId) {
        generationQueue.updateJobId(tempJobId, actualJobId);
      }

      return { job_id: actualJobId };
    } catch (error) {
      console.error('Error in solveProblemWithQueue:', error);
      generationQueue.removeJob(tempJobId);
      throw error;
    }
  }, [generationQueue]);

  // PDF batch problem solving
  const solvePdfProblemsWithQueue = useCallback(async (questions, userId, options = {}) => {
    try {
      const data = await apiService.solvePdfQuestions({
        questions: questions,
        user_id: userId,
        model: options.model || 'mode_fast_37',
      });

      if (!data.success) {
        throw new Error('PDF solving failed');
      }

      if (!data.job_ids || data.job_ids.length === 0) {
        throw new Error('No job IDs returned from backend');
      }

      // Add each job to the generation queue (each subscribes to Firestore listener)
      data.job_ids.forEach((jobId, index) => {
        const questionText = index < questions.length ? questions[index] : null;
        const title = questionText?.substring(0, 80) || `PDF Question ${index + 1}`;

        generationQueue.addJob({
          jobId: jobId,
          type: 'problem',
          title: title,
          metadata: {
            from_pdf: true,
            question_number: index + 1,
            total_questions: data.job_ids.length,
            credits: 1
          }
        });
      });

      return {
        success: true,
        job_ids: data.job_ids,
        jobs_created: data.jobs_created,
        credits_used: data.credits_used,
        remaining_credits: data.remaining_credits
      };
    } catch (error) {
      console.error('Error in solvePdfProblemsWithQueue:', error);
      throw error;
    }
  }, [generationQueue]);

  // Unified generation (topic or problem, auto-classified)
  const generateUnifiedWithQueue = useCallback(async (text, userId, options = {}) => {
    const tempJobId = `temp-${Date.now()}`;

    try {
      generationQueue.addJob({
        jobId: tempJobId,
        type: 'video',
        source: 'home',
        title: text?.substring(0, 100) || 'Generating...',
        metadata: {
          credits: userId ? 1 : 0,
          unified: true,
          anonymous: !userId,
        }
      });

      const startResponse = await apiService.startUnifiedGeneration({
        text,
        user_id: userId || '',
        model: options.model || 'mode_fast_37',
        dry_run: Boolean(options.dry_run),
        is_global: Boolean(options.is_global),
        content_type: options.content_type,
        parent_video_id: options.parent_video_id,
        language: options.language,
      });

      const actualJobId = startResponse.job_id;
      if (actualJobId && actualJobId !== tempJobId) {
        generationQueue.updateJobId(tempJobId, actualJobId);
      }

      return { job_id: actualJobId, ...startResponse };
    } catch (error) {
      console.error('Error in generateUnifiedWithQueue:', error);
      generationQueue.removeJob(tempJobId);
      throw error;
    }
  }, [generationQueue]);

  // Unified batch generation
  const generateUnifiedBatchWithQueue = useCallback(async (texts, userId, options = {}) => {
    try {
      const result = await apiService.startUnifiedBatchGeneration({
        texts,
        user_id: userId,
        model: options.model || 'mode_fast_37',
        dry_run: Boolean(options.dry_run),
        is_global: Boolean(options.is_global),
        content_type: options.content_type,
      });

      if (!result.job_ids || result.job_ids.length === 0) {
        throw new Error('No job IDs returned from backend');
      }

      result.job_ids.forEach((jobId, index) => {
        const title = texts[index]?.substring(0, 100) || `Batch item ${index + 1}`;
        const classification = result.classifications?.[index] || 'unknown';
        const jobType = classification === 'solution' ? 'problem' : 'video';

        generationQueue.addJob({
          jobId,
          type: jobType,
          title,
          metadata: {
            credits: classification === 'long_lesson' ? 2 : 1,
            unified: true,
            batch: true,
            classification,
          }
        });
      });

      return result;
    } catch (error) {
      console.error('Error in generateUnifiedBatchWithQueue:', error);
      throw error;
    }
  }, [generationQueue]);

  // Return all API methods with queue-enhanced versions
  return {
    ...api,
    generateVideo: generateVideoWithQueue,
    solveProblem: solveProblemWithQueue,
    solvePdfProblems: solvePdfProblemsWithQueue,
    generateUnified: generateUnifiedWithQueue,
    generateUnifiedBatch: generateUnifiedBatchWithQueue,

    generateCourseWithQueue: useCallback(async (courseData, userId) => {
      try {
        const apiResult = await api.generateCourse(courseData, userId);

        if (apiResult && apiResult.job_id && generationQueue) {
          generationQueue.addJob({
            jobId: apiResult.job_id,
            type: 'course',
            title: courseData.goal || 'New Course',
            metadata: {
              backgroundKnowledge: courseData.backgroundKnowledge,
              detailLevel: courseData.detailLevel,
              credits: 3
            }
          });
          return { jobId: apiResult.job_id };
        } else {
          return {};
        }
      } catch (error) {
        console.error('Error in generateCourseWithQueue:', error);
        throw error;
      }
    }, [api, generationQueue]),

    generateLessonWithQueue: async (courseId, lessonIndex, userId, lessonTitle) => {
      try {
        const result = await apiService.generateLesson(courseId, {
          course_id: courseId,
          lesson_index: lessonIndex,
          user_id: userId,
          dry_run: false
        });

        if (!result.job_id) {
          throw new Error('No job ID returned from API');
        }

        generationQueue.addJob({
          jobId: result.job_id,
          type: 'course_video',
          title: lessonTitle || `Lesson ${lessonIndex + 1}`,
          metadata: {
            courseId,
            lessonIndex,
            credits: 0
          }
        });

        return result;
      } catch (error) {
        console.error('Error starting lesson generation:', error);
        throw error;
      }
    },

    generateAllLessonsWithQueue: async (courseId, lessonIndices, userId, course) => {
      try {
        const result = await apiService.generateAllLessons(courseId, {
          course_id: courseId,
          user_id: userId,
          lesson_indices: lessonIndices
        });

        result.job_ids.forEach((jobId, idx) => {
          const lessonIndex = lessonIndices[idx];
          const lesson = course?.lessons?.[lessonIndex];

          generationQueue.addJob({
            jobId,
            type: 'course_video',
            title: lesson?.title || `Lesson ${lessonIndex + 1}`,
            metadata: {
              courseId,
              lessonIndex,
              credits: 0
            }
          });
        });

        return result;
      } catch (error) {
        throw error;
      }
    }
  };
};
